import React from 'react'
import { Box, Stack, IconButton, Typography, Dialog, Paper, ToggleButton, ToggleButtonGroup } from '@mui/material'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import { blue, blueGrey, grey } from '@mui/material/colors'
import MenuIcon from '@mui/icons-material/Menu'
import { appName, isPrivateMode, isPublicMode } from 'common/config'
import iconPng from 'assets/icon.png'
import { useGlobalState } from 'state'
import { Map } from 'components/Map'
import { ObservatoryContainer } from 'components/containers/ObservatoryContainer'
import { MobileMenuDialog } from 'components/dialogs/MobileMenuDialog'
import { LoadingDialog } from 'components/dialogs/LoadingDialog'
import { LegendControl } from 'components/controls/LegendControl'
import { ZoomControl } from 'components/controls/ZoomControl'
import { OpacityControl } from 'components/controls/OpacityControl'
import { BorderLayer } from 'components/layers/BorderLayer'
import { TimeSeriesSliderLayers } from 'components/layers/TimeSeriesSliderLayers'
import { ObservatoryLayer } from 'components/layers/ObservatoryLayer'
import { RoadRegulationLayer } from 'components/layers/RoadRegulationLayer'
import { HazardMapLayer } from 'components/layers/HazardMapLayer'
import { GsiVectorLayer } from 'components/layers/GsiVectorLayer'
import { BuildingPlateauLayer } from 'components/layers/BuildingPlateauLayer'
import { HazardMapLegendControl } from 'components/controls/HazardMapLegendControl'
import { CurrentLocationControl } from 'components/controls/CurrentLocationControl'
import { HinanAreaControl } from 'components/controls/HinanAreaControl'
import { NoticeDialog } from 'components/dialogs/NoticeDialog'
import { RoadRegulationControl } from 'components/controls/RoadRegulationControl'
import CircleIcon from '@mui/icons-material/Circle'
import { HinanListControl } from 'components/controls/HinanListControl'
import { EstimatedFloodingLegendControl } from 'components/controls/EstimatedFloodingLegendControl'
import { HinanAreaLayer } from 'components/layers/HinanAreaLayer'
import { HinanAreaNameLayer } from 'components/layers/HinanAreaNameLayer'
import { TrialOperationControl } from 'components/controls/TrialOperationControl'
import { MobileLocationLayer } from 'components/layers/MobileLocationLayer'
import { LegendControlButton } from 'components/ui/LegendControlButton'

const MobileToggleButton = ({value, label, checked}: {value: string, label: string, checked: boolean}) => {
  return (
    <ToggleButton value={value} sx={{fontSize: '11px'}}>
      <Stack direction={'row'} alignItems={'center'}>
        <CircleIcon fontSize='inherit' sx={{color: checked ? blue[700] : grey[300], mr: '4px'}} />
        <Typography variant='caption'>{label}</Typography>
      </Stack>
    </ToggleButton>
  )
}

export const Mobile = () => {
  const state = useGlobalState()
  const [menuDialog, setMenuDialog] = React.useState<boolean>(false)
  const [controls, setControls] = React.useState<string[]>([])
  const [visibleLegend, setVisibleLegend] = React.useState<boolean>(false)

  const isRiverFloods = React.useMemo(() => {
    return state.app.menu === 'river-floods'
  }, [state.app.menu])

  const theme = createTheme({
    palette: {
      primary: {
        main: blueGrey[800]
      },
    },
  })

  return (
    <ThemeProvider theme={theme}>
      { isPublicMode && <NoticeDialog /> }
      <MobileMenuDialog open={menuDialog} onClose={() => setMenuDialog(false)} />
      <Stack>
        <Stack height={'34px'} direction={'row'} alignItems={'center'} pl={1} sx={{backgroundColor: blueGrey[800]}}>
          <img src={iconPng} alt={appName} width={20} height={20} />
          <Typography sx={{ flexGrow: 1, color: 'white', fontSize: '16px', pl: 1, fontWeight: 'bold' }}>{appName}</Typography>
          <IconButton sx={{color: 'white'}} onClick={() => setMenuDialog(true)}>
            <MenuIcon />
          </IconButton>
        </Stack>
        <Box height={'calc(100dvh - 34px)'} position={'relative'} width={'100%'}>
          <Map>
            { (isRiverFloods || isPrivateMode) && <HazardMapLayer /> }

            { isPublicMode && <MobileLocationLayer /> }

            <Stack sx={{position: 'absolute', zIndex: 1, top: 10, right: 10, pointerEvents: 'none'}} alignItems={'flex-end'} spacing={1}>
              <ZoomControl />
              <CurrentLocationControl />
            </Stack>

            <Stack sx={{position: 'absolute', zIndex: 1, top: isPublicMode ? 190 : 140, right: 2, m: 1, pointerEvents: 'none'}} alignItems={'flex-end'} spacing={1}>
              <LegendControlButton onClick={setVisibleLegend} isVisible={visibleLegend} />
              { visibleLegend && <LegendControl /> }
              { visibleLegend && <HazardMapLegendControl /> }
            </Stack>

            <BorderLayer />
            <GsiVectorLayer />
            <BuildingPlateauLayer />
            <RoadRegulationLayer />

            <Stack direction={'column'} alignItems={'flex-start'} spacing={1} sx={{position: 'absolute', zIndex: 1, top: 10, left: 10}}>
              <Paper>
                <ToggleButtonGroup
                  value={controls}
                  onChange={(e, newControls) => {setControls(newControls)}}
                  size='small'
                >
                  <MobileToggleButton value='observatory' label='観測所' checked={controls.includes('observatory')} />
                  <MobileToggleButton value='setting' label='レイヤ' checked={controls.includes('setting')} />
                  { isRiverFloods &&  <MobileToggleButton value='school' label='避難対象地区一覧' checked={controls.includes('school')} /> }
                </ToggleButtonGroup>
              </Paper>
              { isPublicMode && <TrialOperationControl isMobileOnly={true} /> }
            </Stack>

            <Stack direction={'row'} sx={{position: 'absolute', zIndex: 1, top: 60, left: 10}}>
              <Stack direction={'column'} alignItems={'flex-start'} spacing={1} mr={controls.includes('observatory') || controls.includes('setting') ? 1 : 0}>
                <ObservatoryLayer showControl={controls.includes('observatory')} />
                { controls.includes('setting') && (
                  <>
                    <Stack component={Paper} px={1} pb={1} width={160} spacing={'1px'}>
                      <HinanAreaControl />
                      { isRiverFloods && <EstimatedFloodingLegendControl /> }
                      { isPrivateMode && <RoadRegulationControl /> }
                    </Stack>
                    <OpacityControl />
                  </>
                )}
              </Stack>
              { isRiverFloods &&
                <Stack sx={{backgroundColor: grey[50]}} maxHeight={'calc(100dvh - 260px)'} overflow={'auto'}>
                  { controls.includes('school') && <HinanListControl /> }
                </Stack>
              }
            </Stack>

            { isRiverFloods && (
              <>
                <HinanAreaLayer />
                <HinanAreaNameLayer />
              </>
            )}

            <Stack direction="row" alignItems={'flex-end'} sx={{position: 'absolute', zIndex: 1, bottom: 55, left: 60, right: 30}}>
              <Stack flexGrow={1}>
                <TimeSeriesSliderLayers />
              </Stack>
            </Stack>

          </Map>
        </Box>
      </Stack>
      <Dialog
        onClose={() => state.app.setObservatoryContainer(false)}
        open={state.app.observatoryContainer}
        fullScreen
      >
        <ObservatoryContainer />
      </Dialog>
      <LoadingDialog />
    </ThemeProvider>
  )
}
